import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  QrCodeIcon, 
  DevicePhoneMobileIcon, 
  ChartBarIcon,
  CreditCardIcon,
  UsersIcon,
  ShieldCheckIcon,
  BuildingStorefrontIcon,
  CogIcon,
  BellIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import restaurantLogo from '../assets/restaurant-logo.png';

const Features = () => {
  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerCallback = (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('opacity-100', 'translate-y-0');
          entry.target.classList.remove('opacity-0', 'translate-y-10');
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, { threshold: 0.1 });
    document.querySelectorAll('.reveal-on-scroll').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: QrCodeIcon,
      title: 'QR Code Ordering',
      description: 'Zero app friction. Instant menu access for your diners via high-speed QR technology.',
      benefits: ['No app download required', 'Digital menu updates', 'Multi-language translation']
    },
    {
      icon: DevicePhoneMobileIcon,
      title: 'Progressive Web App',
      description: 'The smoothness of an app with the reach of the web. Fully optimized for all mobile platforms.',
      benefits: ['Fast loading times', 'Zero installation', 'Adaptive layout']
    },
    {
      icon: ChartBarIcon,
      title: 'Real-time Analytics',
      description: 'Understand your busy hours and top-selling items with detailed revenue insights.',
      benefits: ['Sales growth tracking', 'Order volume heatmaps', 'Customer behavior data']
    },
    {
      icon: CreditCardIcon,
      title: 'Omnichannel Payments',
      description: 'Accept UPI, cards, and wallets directly at the table. Faster payment = faster turns.',
      benefits: ['Integrated payment flows', 'Secure 256-bit encryption', 'Direct settlement']
    },
    {
      icon: UsersIcon,
      title: 'Intelligent Staffing',
      description: 'Route orders to the right kitchen station or server automatically based on defined zones.',
      benefits: ['Reduced manual errors', 'Optimized staff movement', 'Order status tracking']
    },
    {
      icon: ShieldCheckIcon,
      title: 'Enterprise Security',
      description: 'Built on world-class infrastructure to ensure 99.9% uptime and data integrity.',
      benefits: ['PCI DSS Compliant', 'Automated backups', 'Robust RLS security']
    }
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white font-sans selection:bg-[#F5A623] selection:text-black">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-[#0D0D0D]/90 py-4 px-6 border-b border-white/10 backdrop-blur-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={restaurantLogo} alt="Ordyrr Logo" className="h-8 md:h-10 w-auto object-contain drop-shadow-[0_0_8px_rgba(255,255,255,0.1)]" />
          </Link>
          <Link to="/" className="text-sm font-bold text-gray-400 hover:text-white transition-colors flex items-center gap-2">
            <ArrowLeftIcon className="w-4 h-4" />
            Back Home
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-48 md:pb-32 px-6">
        <div className="max-w-4xl mx-auto text-center reveal-on-scroll opacity-0 translate-y-10 transition-all duration-1000 ease-out">
          <div className="inline-block border border-[#F5A623]/30 bg-[#F5A623]/5 rounded-full px-4 py-1.5 mb-8 text-xs font-bold tracking-wider text-[#F5A623]">
            BUILT FOR GROWTH
          </div>
          <h1 className="text-5xl md:text-7xl font-black tracking-tighter mb-8 leading-[1.05]">
            Engineered for the<br/><span className="text-white">modern kitchen.</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto font-light leading-relaxed">
            From QR menus to intelligent analytics, we provide the full stack your restaurant needs to operate at peak efficiency.
          </p>
        </div>

        {/* Features Grid */}
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-20 mt-20 md:mt-32 px-6">
          {features.map((feature, i) => (
            <div 
              key={i} 
              className="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-1000 ease-out"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="w-14 h-14 bg-[#111111] border border-white/10 rounded-2xl flex items-center justify-center mb-8 shadow-lg shadow-black/20">
                <feature.icon className="w-7 h-7 text-[#F5A623]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 tracking-tight">{feature.title}</h3>
              <p className="text-gray-400 font-light leading-relaxed mb-8 text-base">
                {feature.description}
              </p>
              <ul className="space-y-3">
                {feature.benefits.map((benefit, j) => (
                  <li key={j} className="flex items-center gap-3 text-sm text-gray-500 font-medium">
                    <div className="w-1 h-1 bg-[#F5A623] rounded-full"></div>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Deep Dive Section */}
      <section className="py-24 md:py-32 px-6 bg-[#111111] border-y border-white/5">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-20 items-center">
          <div className="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-1000 ease-out">
            <h2 className="text-3xl md:text-5xl font-black mb-8 tracking-tighter">Manage everything,<br/>from anywhere.</h2>
            <p className="text-gray-400 text-lg font-light leading-relaxed mb-10">
              Our cloud-based dashboard gives you absolute control over your menu, staff, and locations in real-time. Whether you are at the restaurant or on the go.
            </p>
            <div className="space-y-6">
              {[
                { title: "Multi-location ready", desc: "Manage multiple outlets from a single master account." },
                { title: "Menu sync", desc: "Change prices or availability across all tables instantly." },
                { title: "Staff Audit Trail", desc: "Track every staff action for complete operational transparency." }
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-6 h-6 rounded-full border border-[#F5A623] flex items-center justify-center shrink-0 mt-1">
                    <div className="w-2 h-2 bg-[#F5A623] rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-1">{item.title}</h4>
                    <p className="text-sm text-gray-500 font-light">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="reveal-on-scroll opacity-0 scale-95 transition-all duration-1000 delay-200 ease-out bg-[#0D0D0D] border border-white/10 rounded-3xl p-4 shadow-2xl overflow-hidden relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-[#F5A623]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="bg-[#1A1A1A] rounded-2xl p-6 border border-white/5 h-[400px] flex flex-col items-center justify-center text-center">
              <BuildingStorefrontIcon className="w-16 h-16 text-[#F5A623] mb-6 animate-pulse" />
              <div className="text-2xl font-black mb-2">Central Dashboard</div>
              <p className="text-sm text-gray-400 font-light max-w-xs">Connecting dots between your kitchen, your staff, and your customers.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6">
        <div className="max-w-4xl mx-auto text-center reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 ease-out">
          <h2 className="text-4xl md:text-5xl font-black mb-8">Experience the power<br/>of Ordyrr.</h2>
          <Link to="/auth" className="inline-block bg-[#F5A623] hover:bg-[#E0961E] text-black px-10 py-5 rounded-full text-lg font-bold transition-all transform hover:scale-105">
            Start Your Free Trial
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] pt-20 pb-10 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-10">
          <div>
            <div className="mb-4">
              <img src={restaurantLogo} alt="Ordyrr Logo" className="h-8 md:h-10 w-auto object-contain" />
            </div>
            <p className="text-sm text-gray-500 max-w-xs font-light">
              Premium QR ordering and management platform for modern restaurants.
            </p>
          </div>
          <div className="flex gap-8 md:gap-16">
            <Link to="/pricing" className="text-sm text-gray-400 hover:text-white transition-colors">Pricing</Link>
            <Link to="/contact" className="text-sm text-gray-400 hover:text-white transition-colors">Contact</Link>
            <Link to="/help" className="text-sm text-gray-400 hover:text-white transition-colors">Support</Link>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-16 pt-8 border-t border-white/10 text-center text-xs text-gray-600">
          <p>&copy; {new Date().getFullYear()} Ordyrr. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Features;
